import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Plus,
  Trash2,
  Download,
  FileImage,
  RotateCcw,
  Save,
  FolderOpen,
} from "lucide-react";
import * as htmlToImage from "html-to-image";
import ScheduleExport from "./components/ScheduleExport";

const BabyScheduleApp = () => {
  const scheduleRef = useRef(null);
  const exportRef = useRef(null);

  // 有効なアクティビティタイプの定義（データバリデーション用）
  const VALID_ACTIVITY_TYPES = ['breastfeeding', 'medicine', 'weaning'];

  // デフォルトのスケジュール
  const defaultActivities = [
    { id: 1, time: "02:00", type: "breastfeeding", amount: "180ml", note: "" },
    { id: 2, time: "07:00", type: "breastfeeding", amount: "150ml", note: "" },
    { id: 3, time: "07:30", type: "medicine", note: "シロップ 3ml" },
    { id: 4, time: "11:30", type: "breastfeeding", amount: "120ml", note: "" },
    { id: 6, time: "17:00", type: "weaning", note: "1回食" },
    { id: 7, time: "17:30", type: "breastfeeding", amount: "180ml", note: "" },
    { id: 5, time: "18:00", type: "medicine", note: "シロップ 3ml" },
    { id: 8, time: "22:00", type: "breastfeeding", amount: "180ml", note: "" },
  ];

  const defaultDaycareHours = {
    enabled: false,
    start: "08:30",
    end: "16:00",
  };

  // LocalStorageから読み込み、なければデフォルト値を使用
  // 無効なtypeのデータは自動的にフィルタリング
  const [activities, setActivities] = useState(() => {
    const saved = localStorage.getItem("babyScheduleActivities");
    if (!saved) return defaultActivities;

    try {
      const parsed = JSON.parse(saved);
      // 有効なtypeのみを保持
      const filtered = parsed.filter(activity =>
        VALID_ACTIVITY_TYPES.includes(activity.type)
      );

      // フィルタリングでデータが減った場合は開発モードで警告
      if (parsed.length !== filtered.length) {
        console.warn(`[データクリーンアップ] 無効なアクティビティ ${parsed.length - filtered.length}件を除外しました`);
      }

      return filtered.length > 0 ? filtered : defaultActivities;
    } catch (error) {
      console.error('[データ読み込みエラー]', error);
      return defaultActivities;
    }
  });

  const [daycareHours, setDaycareHours] = useState(() => {
    const saved = localStorage.getItem("babyScheduleDaycareHours");
    return saved ? JSON.parse(saved) : defaultDaycareHours;
  });

  // activitiesが変更されたらLocalStorageに保存
  useEffect(() => {
    localStorage.setItem("babyScheduleActivities", JSON.stringify(activities));
  }, [activities]);

  // daycareHoursが変更されたらLocalStorageに保存
  useEffect(() => {
    localStorage.setItem(
      "babyScheduleDaycareHours",
      JSON.stringify(daycareHours)
    );
  }, [daycareHours]);

  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showVersionMenu, setShowVersionMenu] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [showDaycareSettings, setShowDaycareSettings] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [babyInfo, setBabyInfo] = useState(() => {
    const saved = localStorage.getItem("babyInfo");
    return saved
      ? JSON.parse(saved)
      : { nickname: "とまとちゃん", birthday: "" };
  });

  // 月齢を計算する関数
  const calculateAge = (birthday) => {
    if (!birthday) return "";

    const birthDate = new Date(birthday);
    const today = new Date();

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    if (years === 0 && months === 0) {
      return `${days}日`;
    } else if (years === 0) {
      return days > 0 ? `${months}ヶ月${days}日` : `${months}ヶ月`;
    } else {
      return days > 0
        ? `${years}歳${months}ヶ月${days}日`
        : `${years}歳${months}ヶ月`;
    }
  };

  // 時刻が保育園時間帯内かチェック
  const isWithinDaycareHours = (time) => {
    if (!daycareHours.enabled) return false;

    const [hour, minute] = time.split(":").map(Number);
    const [startHour, startMinute] = daycareHours.start.split(":").map(Number);
    const [endHour, endMinute] = daycareHours.end.split(":").map(Number);

    const timeInMinutes = hour * 60 + minute;
    const startInMinutes = startHour * 60 + startMinute;
    const endInMinutes = endHour * 60 + endMinute;

    return timeInMinutes >= startInMinutes && timeInMinutes <= endInMinutes;
  };

  // カスタムモーダル用のstate
  const [customModal, setCustomModal] = useState({
    show: false,
    type: "", // 'input' or 'confirm'
    title: "",
    message: "",
    inputValue: "",
    onConfirm: null,
    onCancel: null,
  });
  const [newActivity, setNewActivity] = useState({
    time: "12:00",
    type: "breastfeeding",
    amount: "120ml",
    note: "",
  });
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [showAd, setShowAd] = useState(false);
  const [devMode, setDevMode] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);

  // babyInfoが変更されたらLocalStorageに保存
  useEffect(() => {
    localStorage.setItem("babyInfo", JSON.stringify(babyInfo));
  }, [babyInfo]);

  // モーダル開閉時の処理（スクロール位置保存・復元、キーボード閉じる）
  useEffect(() => {
    const scrollContainer = document.querySelector('.schedule-scroll-container');
    if (!scrollContainer) return;

    if (customModal.show) {
      // モーダルを開く時：現在のスクロール位置を保存
      setSavedScrollPosition(scrollContainer.scrollTop);
    } else {
      // モーダルを閉じる時：保存した位置に復元 + キーボードを閉じる（入力系要素のみ）
      const activeElement = document.activeElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        activeElement.blur();
      }
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = savedScrollPosition;
      });
    }
  }, [customModal.show, savedScrollPosition]);

  // スタートスクリーンを3秒後に非表示
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowStartScreen(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // ページ全体のスクロールを無効化（モバイル対応）
  useEffect(() => {
    if (!showStartScreen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showStartScreen]);

  // スクロール検知で広告表示
  useEffect(() => {
    if (showStartScreen) return;

    const handleScroll = (e) => {
      const element = e.target;
      console.log("Scroll detected:", element.scrollTop);
      if (element.scrollTop > 10) {
        setShowAd(true);
      } else {
        setShowAd(false);
      }
    };

    // DOMの準備を待つ
    const timer = setTimeout(() => {
      const scrollContainer = document.querySelector(
        ".schedule-scroll-container"
      );
      console.log("Scroll container found:", scrollContainer);
      if (scrollContainer) {
        scrollContainer.addEventListener("scroll", handleScroll);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      const scrollContainer = document.querySelector(
        ".schedule-scroll-container"
      );
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [showStartScreen]);

  // バージョン管理
  const [savedVersions, setSavedVersions] = useState(() => {
    const saved = localStorage.getItem("babyScheduleVersions");
    if (!saved) return [];

    try {
      const parsed = JSON.parse(saved);
      // 各バージョンのアクティビティをフィルタリング
      return parsed.map(version => ({
        ...version,
        activities: version.activities.filter(activity =>
          VALID_ACTIVITY_TYPES.includes(activity.type)
        )
      }));
    } catch (error) {
      console.error('[バージョンデータ読み込みエラー]', error);
      return [];
    }
  });

  const activityTypes = {
    breastfeeding: { label: "ミルク", color: "bg-sky-300", icon: "🍼" },
    medicine: { label: "おくすり", color: "bg-pink-300", icon: "💊" },
    weaning: { label: "離乳食", color: "bg-teal-300", icon: "🥄" },
  };

  const addActivity = () => {
    if (newActivity.time && newActivity.type) {
      if (editingId) {
        // 編集モード
        setActivities(
          activities
            .map((a) =>
              a.id === editingId ? { ...newActivity, id: editingId } : a
            )
            .sort((a, b) => {
              const timeA = a.time.split(":").map(Number);
              const timeB = b.time.split(":").map(Number);
              return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1]);
            })
        );
        setEditingId(null);
      } else {
        // 新規追加モード
        setActivities(
          [...activities, { ...newActivity, id: Date.now() }].sort((a, b) => {
            const timeA = a.time.split(":").map(Number);
            const timeB = b.time.split(":").map(Number);
            return timeA[0] * 60 + timeA[1] - (timeB[0] * 60 + timeB[1]);
          })
        );
      }
      setNewActivity({
        time: "12:00",
        type: "breastfeeding",
        amount: "120ml",
        note: "",
      });
      setShowModal(false);
    }
  };

  const editActivity = (activity) => {
    // 時刻を2桁形式に変換
    const [hour, minute] = activity.time.split(":");
    const formattedTime = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

    setNewActivity({
      time: formattedTime,
      type: activity.type,
      amount: activity.amount || "",
      note: activity.note || "",
    });
    setEditingId(activity.id);
    setShowModal(true);
  };

  const cancelEdit = () => {
    setNewActivity({
      time: "12:00",
      type: "breastfeeding",
      amount: "120ml",
      note: "",
    });
    setEditingId(null);
    setShowModal(false);
  };

  const openAddModal = () => {
    setNewActivity({
      time: "12:00",
      type: "breastfeeding",
      amount: "120ml",
      note: "",
    });
    setEditingId(null);
    setShowModal(true);
  };

  const deleteActivity = (id) => {
    setActivities(activities.filter((a) => a.id !== id));
  };

  const saveVersion = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const hour = now.getHours();
    const minute = now.getMinutes();
    const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][now.getDay()];
    const defaultName = `スケジュール ${year}年${month}月${day}日（${dayOfWeek}）${hour}:${String(minute).padStart(2, '0')}`;

    setCustomModal({
      show: true,
      type: "input",
      title: "今のスケジュールを保存",
      message: "スケジュール名",
      inputValue: defaultName,
      onConfirm: (versionName) => {
        if (!versionName) {
          setCustomModal({ ...customModal, show: false });
          return;
        }

        // 3つ以上保存されている場合は上書き確認
        if (savedVersions.length >= 3) {
          setCustomModal({
            show: true,
            type: "confirm",
            title: "上書き確認",
            message:
              "保存できるスケジュールは最大3つまでです。\n一番古いスケジュールが削除されますが、よろしいですか？",
            onConfirm: () => {
              saveVersionWithName(versionName);
            },
            onCancel: () => {
              setCustomModal({ ...customModal, show: false });
            },
          });
        } else {
          saveVersionWithName(versionName);
        }
      },
      onCancel: () => {
        setCustomModal({ ...customModal, show: false });
      },
    });
  };

  const saveVersionWithName = (versionName) => {
    const newVersion = {
      id: Date.now(),
      name: versionName,
      date: new Date().toISOString(),
      activities: activities,
    };

    const updatedVersions = [newVersion, ...savedVersions].slice(0, 3);
    setSavedVersions(updatedVersions);
    localStorage.setItem(
      "babyScheduleVersions",
      JSON.stringify(updatedVersions)
    );
    setCustomModal({ ...customModal, show: false });
  };

  const loadVersion = (version) => {
    setCustomModal({
      show: true,
      type: "confirm",
      title: "スケジュールを読み込む",
      message: `「${version.name}」を読み込みますか？現在の編集内容は失われます。`,
      onConfirm: () => {
        setActivities(version.activities);
        setShowVersionMenu(false);
        setCustomModal({ ...customModal, show: false });
      },
      onCancel: () => {
        setCustomModal({ ...customModal, show: false });
      },
    });
  };

  const deleteVersion = (versionId) => {
    setCustomModal({
      show: true,
      type: "confirm",
      title: "削除確認",
      message: "この保存データを削除しますか？",
      onConfirm: () => {
        const updatedVersions = savedVersions.filter((v) => v.id !== versionId);
        setSavedVersions(updatedVersions);
        localStorage.setItem(
          "babyScheduleVersions",
          JSON.stringify(updatedVersions)
        );
        setCustomModal({ ...customModal, show: false });
      },
      onCancel: () => {
        setCustomModal({ ...customModal, show: false });
      },
    });
  };

  const resetSchedule = () => {
    setCustomModal({
      show: true,
      type: "confirm",
      title: "デフォルトに戻す",
      message: "スケジュールをデフォルトに戻しますか？",
      onConfirm: () => {
        setActivities(defaultActivities);
        localStorage.removeItem("babyScheduleActivities");
        setCustomModal({ ...customModal, show: false });
      },
      onCancel: () => {
        setCustomModal({ ...customModal, show: false });
      },
    });
  };

  const exportAsImage = async () => {
    if (!exportRef.current) return;

    try {
      // ドロップダウンを閉じる
      setShowExportMenu(false);

      // エクスポート中フラグをオン
      setIsExporting(true);

      // フォント読み込みを待機
      await document.fonts.ready;

      // DOM更新を待つ
      await new Promise((resolve) => requestAnimationFrame(resolve));

      // toBlobを使用（メモリ効率が良い）
      const blob = await htmlToImage.toBlob(exportRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        cacheBust: true,
        skipFonts: true, // 外部フォントのCORSエラーを回避
      });

      if (!blob) {
        throw new Error("画像の生成に失敗しました");
      }

      // モバイル判定（iPhone + Android）
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile) {
        // モバイル: 全画面プレビュー表示
        const url = URL.createObjectURL(blob);
        setImagePreview(url);
      } else {
        // PC: ダウンロード方式
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `育児スケジュール_${new Date()
          .toLocaleDateString("ja-JP")
          .replace(/\//g, "-")}.png`;
        link.href = url;
        link.click();

        // クリーンアップ（ダウンロード開始を待ってから実行）
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
      }
    } catch (error) {
      console.error("画像エクスポートエラー:", error);
      alert("画像のエクスポートに失敗しました: " + error.message);
    } finally {
      // エクスポート中フラグをオフ
      setIsExporting(false);
    }
  };

  if (showStartScreen) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50 py-8 px-6">
        <img
          src="/gokusukeStart.png"
          alt="Start Screen"
          className="max-w-full max-h-full object-contain"
        />
      </div>
    );
  }

  return (
    <>
    <div className="bg-gradient-to-br from-pink-50/50 via-white to-purple-50/50 fixed inset-0 overflow-hidden flex items-center justify-center py-4 px-4">
      <div
        ref={scheduleRef}
        className="bg-white rounded-2xl shadow-xl pt-8 px-4 max-w-[360px] w-full max-h-[640px] overflow-hidden flex flex-col"
      >
        <div className="flex justify-between items-center mt-4 mb-4 flex-shrink-0">
          {/* 左ブロック */}
          <div className="flex-1">
            {editingTitle ? (
              <div className="flex flex-col gap-2 bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">
                    子どものニックネーム
                  </label>
                  <input
                    type="text"
                    value={babyInfo.nickname}
                    onChange={(e) =>
                      setBabyInfo({ ...babyInfo, nickname: e.target.value })
                    }
                    className="text-base font-bold border-b border-gray-300 focus:outline-none focus:border-cyan-500 px-1 py-1"
                    placeholder="ニックネーム"
                    autoFocus
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500">お誕生日</label>
                  <input
                    type="date"
                    value={babyInfo.birthday}
                    onChange={(e) =>
                      setBabyInfo({ ...babyInfo, birthday: e.target.value })
                    }
                    className="text-base text-gray-600 border border-gray-300 rounded focus:outline-none focus:border-cyan-500 px-2 py-1"
                  />
                </div>
                <button
                  onClick={() => {
                    setEditingTitle(false);
                    setShowDaycareSettings(true);
                  }}
                  className="text-xs bg-amber-500 text-white py-1.5 px-3 rounded hover:bg-amber-600 transition font-medium flex items-center justify-center gap-1"
                >
                  🏫 保育園設定
                </button>
                <button
                  onClick={() => setEditingTitle(false)}
                  className="text-xs bg-cyan-500 text-white py-1.5 px-3 rounded hover:bg-cyan-600 transition font-medium"
                >
                  完了
                </button>
                <a
                  href="/privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-500 hover:text-gray-700 underline text-center"
                >
                  プライバシーポリシー
                </a>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <img
                  src="/mokubaICOOON.png"
                  alt="装飾"
                  className="w-3.5 h-3.5"
                />
                <div
                  onClick={() => {
                    const newCount = clickCount + 1;
                    setClickCount(newCount);
                    if (newCount >= 5) {
                      setDevMode(!devMode);
                      setClickCount(0);
                    }
                  }}
                >
                  <h1 className="text-sm font-bold text-gray-800">
                    {babyInfo.nickname}
                  </h1>
                  <p className="text-xs text-gray-600">
                    {calculateAge(babyInfo.birthday)}
                  </p>
                </div>
                <button
                  onClick={() => setEditingTitle(true)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <img
                    src="/settingGear.png"
                    alt="設定"
                    className="w-3.5 h-3.5"
                  />
                </button>
                {devMode && (
                  <button
                    onClick={resetSchedule}
                    className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-500 rounded hover:bg-gray-200 transition"
                    title="デフォルトに戻す"
                  >
                    <RotateCcw size={10} />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* 右ブロック */}
          <div className="flex flex-col gap-1.5">
            <div className="flex gap-1.5 relative">
              <button
                onClick={saveVersion}
                className="p-1.5 bg-green-100 text-green-600 rounded hover:bg-green-200 transition"
                title="現在のスケジュールを保存（最大3つまで）"
              >
                <Save size={16} />
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowVersionMenu(!showVersionMenu)}
                  className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition"
                  title="保存したスケジュールを開く"
                >
                  <FolderOpen size={16} />
                </button>

                {showVersionMenu && (
                  <>
                    {/* 半透明カバー */}
                    <div className="fixed inset-0 bg-[rgba(0,0,0,0.5)] z-40"></div>

                    {/* モーダル */}
                    <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md bg-white rounded-2xl shadow-2xl z-50 p-6 max-h-[80vh] flex flex-col">
                      <h3 className="text-lg font-bold text-gray-800 mb-4">
                        保存されたスケジュール
                      </h3>
                      <div className="flex-1 overflow-y-auto">
                        {savedVersions.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-gray-500 text-center">
                            保存されたスケジュールはありません
                          </div>
                        ) : (
                          savedVersions.map((version) => {
                            // サマリーを計算
                            const summary = version.activities.reduce(
                              (acc, activity) => {
                                const type = activityTypes[activity.type].label;
                                acc[type] = (acc[type] || 0) + 1;
                                return acc;
                              },
                              {}
                            );
                            const summaryText = Object.entries(summary)
                              .map(([type, count]) => `${type}${count}回`)
                              .join("、");

                            return (
                              <div
                                key={version.id}
                                className="border-b border-gray-100 last:border-b-0"
                              >
                                <div className="px-4 py-3 hover:bg-gray-50">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-gray-800">
                                      {version.name}
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteVersion(version.id);
                                      }}
                                      className="text-gray-400 hover:text-red-500 transition"
                                      title="削除"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    {new Date(version.date).toLocaleString(
                                      "ja-JP"
                                    )}
                                  </div>
                                  <div className="text-xs text-cyan-600 mb-2">
                                    {summaryText}
                                  </div>
                                  <button
                                    onClick={() => loadVersion(version)}
                                    className="w-full px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition"
                                  >
                                    このスケジュールを読み込む
                                  </button>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      <div className="border-t border-gray-200 p-3 flex justify-center">
                        <button
                          onClick={() => setShowVersionMenu(false)}
                          className="text-sm text-gray-600 hover:text-gray-800 underline decoration-gray-400 hover:decoration-gray-600 transition"
                        >
                          × 閉じる
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="p-1.5 bg-purple-100 text-purple-600 rounded hover:bg-purple-200 transition"
                  title="スケジュールを画像・PDFで保存"
                >
                  <Download size={16} />
                </button>

                {showExportMenu && (
                  <>
                    {/* 半透明カバー */}
                    <div className="fixed inset-0 bg-[rgba(0,0,0,0.5)] z-40"></div>

                    {/* モーダル */}
                    <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md bg-white rounded-2xl shadow-2xl z-50 p-6 flex flex-col">
                      <div className="mb-4">
                        <h3 className="text-lg font-bold text-gray-800 mb-2">
                          スケジュールを画像にする
                        </h3>
                        <p className="text-sm text-gray-600">
                          【保存した画像の活用方法】
                          <br />
                          ・育児のサポートアイテムとして✨
                          <br />
                          ・ご家族などと共有✨
                        </p>
                      </div>
                      <button
                        onClick={exportAsImage}
                        className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition font-medium mb-4"
                        title="PNG画像として保存"
                      >
                        <FileImage size={22} />
                        <span className="text-base">画像を保存</span>
                      </button>
                      <div className="border-t border-gray-200 pt-3 flex justify-center">
                        <button
                          onClick={() => setShowExportMenu(false)}
                          className="text-sm text-gray-600 hover:text-gray-800 underline decoration-gray-400 hover:decoration-gray-600 transition"
                        >
                          × 閉じる
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* <button
              onClick={openAddModal}
              className="w-full p-1.5 bg-cyan-500 text-white rounded hover:bg-cyan-600 transition flex items-center justify-center gap-1 text-xs font-medium"
              title="新しい活動を追加"
            >
              <Plus size={16} />
              <span>活動を追加</span>
            </button> */}
          </div>
        </div>

        {showModal && (
          <>
            {/* 半透明カバー */}
            <div
              className="fixed inset-0 bg-[rgba(0,0,0,0.5)] z-40"
              onClick={cancelEdit}
            ></div>

            {/* モーダル */}
            <div
              className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 w-[calc(100%-2rem)] max-w-md z-50 flex flex-col max-h-[96vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingId ? "活動を編集" : "新しい活動を追加"}
                </h3>
                <button
                  onClick={cancelEdit}
                  className="text-gray-400 hover:text-gray-600 transition text-2xl leading-none"
                  title="閉じる"
                >
                  ✕
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    時間
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={
                        newActivity.time ? newActivity.time.split(":")[0] : ""
                      }
                      onChange={(e) => {
                        const minute = newActivity.time
                          ? newActivity.time.split(":")[1]
                          : "00";
                        setNewActivity({
                          ...newActivity,
                          time: `${e.target.value}:${minute}`,
                        });
                      }}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    >
                      <option value="">時</option>
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={String(i).padStart(2, "0")}>
                          {String(i).padStart(2, "0")}時
                        </option>
                      ))}
                    </select>
                    <select
                      value={
                        newActivity.time ? newActivity.time.split(":")[1] : ""
                      }
                      onChange={(e) => {
                        const hour = newActivity.time
                          ? newActivity.time.split(":")[0]
                          : "00";
                        setNewActivity({
                          ...newActivity,
                          time: `${hour}:${e.target.value}`,
                        });
                      }}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    >
                      <option value="">分</option>
                      {Array.from({ length: 12 }, (_, i) => {
                        const minute = i * 5;
                        return (
                          <option
                            key={minute}
                            value={String(minute).padStart(2, "0")}
                          >
                            {String(minute).padStart(2, "0")}分
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    種類
                  </label>
                  <select
                    value={newActivity.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setNewActivity({
                        ...newActivity,
                        type: newType,
                        amount: newType === "breastfeeding" ? "120ml" : "",
                      });
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    {Object.entries(activityTypes).map(
                      ([key, { label, icon }]) => (
                        <option key={key} value={key}>
                          {icon} {label}
                        </option>
                      )
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    量
                  </label>
                  <select
                    value={newActivity.amount}
                    onChange={(e) =>
                      setNewActivity({ ...newActivity, amount: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  >
                    <option value="">選択してください</option>
                    {Array.from({ length: 22 }, (_, i) => (i + 3) * 10).map(
                      (ml) => (
                        <option key={ml} value={`${ml}ml`}>
                          {ml}ml
                        </option>
                      )
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    メモ
                  </label>
                  <input
                    type="text"
                    placeholder="メモを入力"
                    value={newActivity.note}
                    onChange={(e) =>
                      setNewActivity({ ...newActivity, note: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  />
                </div>
              </div>
              <button
                onClick={addActivity}
                className="w-full bg-cyan-500 text-white py-3 rounded-lg hover:bg-cyan-600 transition font-medium mt-6"
              >
                {editingId ? "更新" : "追加"}
              </button>
            </div>
          </>
        )}

        {/* 保育園設定モーダル */}
        {showDaycareSettings && (
          <>
            <div className="fixed inset-0 bg-[rgba(0,0,0,0.5)] z-40" onClick={() => setShowDaycareSettings(false)}></div>
            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 w-[calc(100%-2rem)] max-w-md z-50" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-gray-800 mb-4">🏫 保育園設定</h3>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="daycareEnabled"
                    checked={daycareHours.enabled}
                    onChange={(e) =>
                      setDaycareHours({ ...daycareHours, enabled: e.target.checked })
                    }
                    className="w-4 h-4 text-amber-500 border-gray-300 rounded focus:ring-amber-500"
                  />
                  <label htmlFor="daycareEnabled" className="text-sm font-medium text-gray-700">
                    保育園に通っている
                  </label>
                </div>

                {daycareHours.enabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        保育園開始時刻
                      </label>
                      <input
                        type="time"
                        value={daycareHours.start}
                        onChange={(e) =>
                          setDaycareHours({ ...daycareHours, start: e.target.value })
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        保育園終了時刻
                      </label>
                      <input
                        type="time"
                        value={daycareHours.end}
                        onChange={(e) =>
                          setDaycareHours({ ...daycareHours, end: e.target.value })
                        }
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>

                    <p className="text-xs text-gray-500">
                      この時間帯のミルク・離乳食・おくすりは保育園バナー内に表示されます
                    </p>
                  </>
                )}
              </div>

              <button
                onClick={() => setShowDaycareSettings(false)}
                className="w-full bg-amber-500 text-white py-3 rounded-lg hover:bg-amber-600 transition font-medium mt-6"
              >
                完了
              </button>
            </div>
          </>
        )}

        <div className="relative flex-1 overflow-y-auto schedule-scroll-container">
          <div className="min-h-[600px]">
            {/* スケジュールリスト部分（ラインあり） */}
            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

            {(() => {
              // 保育園時間帯の活動を抽出
              const daycareActivities = daycareHours.enabled
                ? activities.filter(
                    (a) =>
                      (a.type === "breastfeeding" ||
                        a.type === "weaning" ||
                        a.type === "medicine") &&
                      isWithinDaycareHours(a.time)
                  )
                : [];

              // 保育園時間帯の活動を通常リストから除外
              const regularActivities = activities.filter(
                (a) =>
                  !(
                    daycareHours.enabled &&
                    (a.type === "breastfeeding" ||
                      a.type === "weaning" ||
                      a.type === "medicine") &&
                    isWithinDaycareHours(a.time)
                  )
              );

              // 仮想的な保育園バナーアイテムを作成
              const items = [...regularActivities];

              if (daycareHours.enabled && daycareActivities.length > 0) {
                items.push({
                  id: "daycare-banner",
                  time: daycareHours.start,
                  type: "daycare-banner",
                });
              }

              // 時刻でソート
              items.sort((a, b) => {
                const [aHour, aMin] = a.time.split(":").map(Number);
                const [bHour, bMin] = b.time.split(":").map(Number);
                return aHour * 60 + aMin - (bHour * 60 + bMin);
              });

              return items.map((item) => {
                // 保育園バナー
                if (item.type === "daycare-banner") {
                  return (
                    <div
                      key={item.id}
                      className="bg-amber-50 border-l-6 border-amber-400 rounded-r-lg p-4 mb-3 ml-6"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-lg">🏫</span>
                        <span className="text-sm font-semibold text-amber-800">
                          保育園 ({daycareHours.start} 〜 {daycareHours.end})
                        </span>
                      </div>
                      <div className="space-y-2">
                        {daycareActivities.map((da) => {
                          const { label, color, icon } = activityTypes[da.type];

                          // ミルクの間隔を計算
                          let milkInterval = "";
                          if (da.type === "breastfeeding") {
                            const milkActivities = activities.filter(
                              (a) => a.type === "breastfeeding"
                            );
                            const currentIndex = milkActivities.findIndex(
                              (m) => m.id === da.id
                            );

                            if (currentIndex > 0) {
                              const prevMilk = milkActivities[currentIndex - 1];
                              const [prevHour, prevMin] = prevMilk.time
                                .split(":")
                                .map(Number);
                              const [currHour, currMin] = da.time
                                .split(":")
                                .map(Number);

                              let totalMinutes =
                                currHour * 60 + currMin - (prevHour * 60 + prevMin);
                              if (totalMinutes < 0) totalMinutes += 24 * 60;

                              const hours = Math.floor(totalMinutes / 60);
                              const minutes = totalMinutes % 60;

                              if (hours > 0 && minutes > 0) {
                                milkInterval = `${hours}時間${minutes}分後`;
                              } else if (hours > 0) {
                                milkInterval = `${hours}時間後`;
                              } else {
                                milkInterval = `${minutes}分後`;
                              }
                            } else if (currentIndex === 0 && milkActivities.length > 1) {
                              const lastMilk = milkActivities[milkActivities.length - 1];
                              const [lastHour, lastMin] = lastMilk.time
                                .split(":")
                                .map(Number);
                              const [firstHour, firstMin] = da.time
                                .split(":")
                                .map(Number);

                              let totalMinutes =
                                firstHour * 60 + firstMin - (lastHour * 60 + lastMin);
                              if (totalMinutes < 0) totalMinutes += 24 * 60;

                              const hours = Math.floor(totalMinutes / 60);
                              const minutes = totalMinutes % 60;

                              if (hours > 0 && minutes > 0) {
                                milkInterval = `${hours}時間${minutes}分後`;
                              } else if (hours > 0) {
                                milkInterval = `${hours}時間後`;
                              } else {
                                milkInterval = `${minutes}分後`;
                              }
                            }
                          }

                          return (
                            <div key={da.id} className="relative flex items-start gap-2">
                              <div
                                className={`w-6 h-6 ${color} rounded flex items-center justify-center text-white flex-shrink-0 shadow-sm mt-1`}
                              >
                                <span className="text-xs">{icon}</span>
                              </div>
                              <div
                                onClick={() => editActivity(da)}
                                className="flex-1 bg-white border border-gray-200 rounded-lg p-2 shadow-sm hover:shadow-md hover:border-cyan-300 transition cursor-pointer"
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-sm font-semibold text-gray-800">
                                      {da.time}
                                    </span>
                                    <span className="text-xs font-semibold text-gray-800">{label}</span>
                                    {da.amount && (
                                      <span className="text-sm font-semibold text-red-400">
                                        {da.amount}
                                      </span>
                                    )}
                                    {milkInterval && (
                                      <span className="text-xs text-red-400 font-medium">
                                        ({milkInterval})
                                      </span>
                                    )}
                                    {da.note && (
                                      <span className="text-xs text-cyan-600">
                                        {da.note}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteActivity(da.id);
                                    }}
                                    className="text-gray-400 hover:text-red-500 transition ml-1"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                // 通常の活動
                const activity = item;
                const { label, color, icon } = activityTypes[activity.type];

              // ミルクの間隔を計算
              let milkInterval = "";
              if (activity.type === "breastfeeding") {
                // 現在のミルクより前のミルクを見つける
                const milkActivities = activities.filter(
                  (a) => a.type === "breastfeeding"
                );
                const currentIndex = milkActivities.findIndex(
                  (m) => m.id === activity.id
                );

                if (currentIndex > 0) {
                  // 2番目以降のミルク：前のミルクからの間隔
                  const prevMilk = milkActivities[currentIndex - 1];
                  const [prevHour, prevMin] = prevMilk.time
                    .split(":")
                    .map(Number);
                  const [currHour, currMin] = activity.time
                    .split(":")
                    .map(Number);

                  let totalMinutes =
                    currHour * 60 + currMin - (prevHour * 60 + prevMin);
                  if (totalMinutes < 0) totalMinutes += 24 * 60; // 日をまたぐ場合

                  const hours = Math.floor(totalMinutes / 60);
                  const minutes = totalMinutes % 60;

                  if (hours > 0 && minutes > 0) {
                    milkInterval = `${hours}時間${minutes}分後`;
                  } else if (hours > 0) {
                    milkInterval = `${hours}時間後`;
                  } else {
                    milkInterval = `${minutes}分後`;
                  }
                } else if (currentIndex === 0 && milkActivities.length > 1) {
                  // 最初のミルク：最後のミルクからの間隔
                  const lastMilk = milkActivities[milkActivities.length - 1];
                  const [lastHour, lastMin] = lastMilk.time
                    .split(":")
                    .map(Number);
                  const [firstHour, firstMin] = activity.time
                    .split(":")
                    .map(Number);

                  let totalMinutes =
                    firstHour * 60 + firstMin - (lastHour * 60 + lastMin);
                  if (totalMinutes < 0) totalMinutes += 24 * 60; // 日をまたぐ場合

                  const hours = Math.floor(totalMinutes / 60);
                  const minutes = totalMinutes % 60;

                  if (hours > 0 && minutes > 0) {
                    milkInterval = `${hours}時間${minutes}分後`;
                  } else if (hours > 0) {
                    milkInterval = `${hours}時間後`;
                  } else {
                    milkInterval = `${minutes}分後`;
                  }
                }
              }

              return (
                  <div key={activity.id} className="relative flex items-start gap-2 mb-3 pr-4">
                    <div
                      className={`w-6 h-6 ${color} rounded flex items-center justify-center text-white z-10 flex-shrink-0 shadow-sm mt-1`}
                    >
                      <span className="text-xs">{icon}</span>
                    </div>

                    <div
                      onClick={() => editActivity(activity)}
                      className="flex-1 bg-white border border-gray-200 rounded-lg p-2 shadow-sm hover:shadow-md hover:border-cyan-300 transition cursor-pointer"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800">
                            {activity.time}
                          </span>
                          <span className="text-xs font-semibold text-gray-800">
                            {label}
                          </span>
                          {activity.amount && (
                            <span className="text-sm font-semibold text-red-400">
                              {activity.amount}
                            </span>
                          )}
                          {milkInterval && (
                            <span className="text-xs text-red-400 font-medium">
                              ({milkInterval})
                            </span>
                          )}
                          {activity.note && (
                            <span className="text-xs text-cyan-600">
                              {activity.note}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteActivity(activity.id);
                          }}
                          className="text-gray-400 hover:text-red-500 transition ml-1"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
              );
            });
          })()}
            </div>

          {/* スケジュール下部の追加ボタン */}
          <div className="mt-3 pr-4">
            <button
              onClick={openAddModal}
              className="w-full p-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition flex items-center justify-center gap-2 text-sm font-medium"
            >
              <Plus size={18} />
              <span>新しい活動を追加</span>
            </button>
          </div>
          </div>
        </div>

        {/* 広告バナー */}
        {/* <div
          className={`transition-all duration-300 ease-in-out ${
            showAd ? "h-[100px] opacity-100" : "h-0 opacity-0"
          } overflow-hidden flex-shrink-0`}
        >
          <div className="h-full bg-gradient-to-r from-blue-50 to-purple-50 border-t border-gray-200 flex items-center justify-center px-4">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">スポンサー広告</p>
              <p className="text-sm font-medium text-gray-700">
                広告エリア（320×100）
              </p>
            </div>
          </div>
        </div> */}
      </div>

      {/* カスタムモーダル */}
      {customModal.show && (
        <>
          {/* 半透明カバー */}
          <div className="fixed inset-0 bg-[rgba(0,0,0,0.5)] z-[60]"></div>

          {/* モーダル本体 */}
          <div
            className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md rounded-2xl shadow-2xl z-[70] p-6 ${
              customModal.title === "上書き確認"
                ? "bg-amber-50 border-2 border-amber-400"
                : "bg-white"
            }`}
          >
            <h3
              className={`text-lg font-bold mb-3 ${
                customModal.title === "上書き確認"
                  ? "text-amber-800"
                  : "text-gray-800"
              }`}
            >
              {customModal.title === "上書き確認" && "⚠️ "}
              {customModal.title}
            </h3>
            <p
              className={`text-sm mb-4 whitespace-pre-line ${
                customModal.title === "上書き確認"
                  ? "text-amber-900"
                  : "text-gray-600"
              }`}
            >
              {customModal.message}
            </p>

            {customModal.type === "input" && (
              <input
                type="text"
                value={customModal.inputValue}
                onChange={(e) =>
                  setCustomModal({ ...customModal, inputValue: e.target.value })
                }
                className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-base"
                autoFocus
              />
            )}

            <div className="flex gap-3">
              <button
                onClick={() => customModal.onCancel?.()}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition font-medium text-sm"
              >
                キャンセル
              </button>
              <button
                onClick={() => {
                  if (customModal.type === "input") {
                    customModal.onConfirm?.(customModal.inputValue);
                  } else {
                    customModal.onConfirm?.();
                  }
                }}
                className={`flex-1 py-3 rounded-lg transition font-medium text-sm ${
                  customModal.title === "上書き確認"
                    ? "bg-amber-500 text-white hover:bg-amber-600"
                    : "bg-cyan-500 text-white hover:bg-cyan-600"
                }`}
              >
                {customModal.type === "input" ? "保存" : "OK"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>

    {/* エクスポート用の非表示DOM（Portalで画面外に配置） */}
    {typeof document !== 'undefined' && createPortal(
      <div style={{
        position: 'fixed',
        top: '-9999px',
        left: 0,
        opacity: 0,
        pointerEvents: 'none',
      }}>
        <ScheduleExport
          ref={exportRef}
          babyInfo={babyInfo}
          activities={activities}
          daycareHours={daycareHours}
          activityTypes={activityTypes}
          calculateAge={calculateAge}
          isWithinDaycareHours={isWithinDaycareHours}
        />
      </div>,
      document.body
    )}

    {/* エクスポート中のローディング表示 */}
    {isExporting && (
      <>
        <div className="fixed inset-0 bg-[rgba(0,0,0,0.5)] z-[80]"></div>
        <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl z-[90] p-8 flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-lg font-medium text-gray-800">画像を生成中...</p>
          <p className="text-sm text-gray-600">少々お待ちください</p>
        </div>
      </>
    )}

    {/* 画像プレビューオーバーレイ（iPhoneでWeb Share API非対応時） */}
    {imagePreview && (
      <div className="fixed inset-0 bg-black z-[100] flex flex-col">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-4 bg-black">
          <button
            onClick={() => {
              URL.revokeObjectURL(imagePreview);
              setImagePreview(null);
            }}
            className="text-white text-lg font-medium"
          >
            閉じる
          </button>
        </div>

        {/* 画像表示エリア */}
        <div className="flex-1 flex items-center justify-center overflow-auto p-4">
          <img
            src={imagePreview}
            alt="スケジュール画像"
            className="max-w-full max-h-full object-contain"
          />
        </div>

        {/* 案内テキスト */}
        <div className="p-6 bg-black text-center">
          <p className="text-white text-base font-medium mb-2">
            画像を長押しして「画像を保存」を選択してください
          </p>
          <p className="text-gray-400 text-sm">
            長押しで保存メニューが表示されます
          </p>
        </div>
      </div>
    )}
    </>
  );
};


export default BabyScheduleApp;
